import { z } from 'zod';
import { paginationSchema, sortOrderSchema } from './helpers';
import { ColumnDataTypes, FilterOperators, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, isValuelessOperator, } from './table';
import { AuditOperation, auditLogSchema } from './audit-logs';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
/**
 * Filterable fields for the audit-logs DataTable. Single source of truth for
 * field keys — referenced by the contract, the backend wrapper filter builder,
 * and the filter-options dispatcher so no raw string literals leak into code.
 */
export const AUDIT_LOGS_FILTERABLE_FIELDS = {
    TIMESTAMP: 'timestamp',
    TABLE_NAME: 'tableName',
    OPERATION: 'operation',
    RECORD_ID: 'recordId',
    USER: 'user',
    CHANGES: 'changes',
};
/**
 * Per-column data types. Timestamp is DATE (range ops only, trimmed in
 * `validateAuditFilterValue`), Object ID is TEXT (EQUALS + contains-family),
 * the rest are ENUM.
 *
 * `recordId` is **not** routed through the shared `createTableQuerySchema`
 * datatype gate: that gate derives operators from `OPERATORS_BY_DATA_TYPE`,
 * whose TEXT set has no EQUALS and includes INCLUDES/EXCLUDES — neither matching
 * what Object ID needs. It is parsed and validated bespoke (`AUDIT_LOGS_RECORD_ID_OPERATORS`)
 * while keeping shared `table.ts` untouched.
 */
export const AUDIT_LOGS_COLUMN_DATA_TYPES = {
    [AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP]: ColumnDataTypes.DATE,
    [AUDIT_LOGS_FILTERABLE_FIELDS.TABLE_NAME]: ColumnDataTypes.ENUM,
    [AUDIT_LOGS_FILTERABLE_FIELDS.OPERATION]: ColumnDataTypes.ENUM,
    [AUDIT_LOGS_FILTERABLE_FIELDS.RECORD_ID]: ColumnDataTypes.TEXT,
    [AUDIT_LOGS_FILTERABLE_FIELDS.USER]: ColumnDataTypes.ENUM,
    [AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES]: ColumnDataTypes.ENUM,
};
/**
 * Fields whose operators ARE expressible by the shared datatype gate. Object ID
 * is omitted and handled bespoke (see `AUDIT_LOGS_COLUMN_DATA_TYPES`).
 */
const SHARED_COLUMN_DATA_TYPES = {
    [AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP]: ColumnDataTypes.DATE,
    [AUDIT_LOGS_FILTERABLE_FIELDS.TABLE_NAME]: ColumnDataTypes.ENUM,
    [AUDIT_LOGS_FILTERABLE_FIELDS.OPERATION]: ColumnDataTypes.ENUM,
    [AUDIT_LOGS_FILTERABLE_FIELDS.USER]: ColumnDataTypes.ENUM,
    [AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES]: ColumnDataTypes.ENUM,
};
/**
 * Operators the Object ID column accepts: exact "Is" + contains-family, plus the
 * value-less Is Empty / Is Not Empty (record_id is nullable). INCLUDES/EXCLUDES
 * are dropped (option-list semantics, no dropdown).
 */
export const AUDIT_LOGS_RECORD_ID_OPERATORS = [
    FilterOperators.EQUALS,
    FilterOperators.CONTAINS,
    FilterOperators.NOT_CONTAINS,
    FilterOperators.STARTS_WITH,
    FilterOperators.ENDS_WITH,
    FilterOperators.IS_EMPTY,
    FilterOperators.IS_NOT_EMPTY,
];
const RECORD_ID_OPERATOR_SET = new Set(AUDIT_LOGS_RECORD_ID_OPERATORS);
/**
 * Operators the Timestamp column accepts: range-only. `EQUALS`/
 * `NOT_EQUALS` are dropped because audit timestamps are millisecond-precision
 * and a day-level "Is" would match ~zero rows. The DATE datatype gate permits
 * EQUALS/NOT_EQUALS, so they are explicitly trimmed in `validateAuditFilterValue`.
 */
export const AUDIT_LOGS_TIMESTAMP_OPERATORS = [
    FilterOperators.GREATER_THAN,
    FilterOperators.LESS_THAN,
    FilterOperators.BETWEEN,
];
const TIMESTAMP_OPERATOR_SET = new Set(AUDIT_LOGS_TIMESTAMP_OPERATORS);
/**
 * Sortable fields. `changes` sorts by the number of changed fields
 * (`changed_fields` key count); rows with no changes (null/empty) sort last.
 */
export const auditLogsTableSortFieldSchema = z.enum([
    AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP,
    AUDIT_LOGS_FILTERABLE_FIELDS.TABLE_NAME,
    AUDIT_LOGS_FILTERABLE_FIELDS.OPERATION,
    AUDIT_LOGS_FILTERABLE_FIELDS.RECORD_ID,
    AUDIT_LOGS_FILTERABLE_FIELDS.USER,
    AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES,
]);
/**
 * Default sort field applied by the backend when `sortBy` is omitted
 * (`timestamp DESC, id DESC`). A valid member of the sort enum.
 */
export const AUDIT_LOGS_DEFAULT_SORT_FIELD = AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/**
 * `tableName[.field]` shape — identifier, optionally a single dot then a second
 * identifier. Rejects empty / leading-dot / trailing-dot / multi-dot.
 */
const CHANGES_VALUE_REGEX = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)?$/;
const AUDIT_OPERATION_VALUES = new Set(Object.values(AuditOperation));
function isUuid(value) {
    return UUID_REGEX.test(value.trim());
}
function isParseableDate(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return false;
    return !Number.isNaN(new Date(trimmed).getTime());
}
/** Trim + percent-decode the whole `recordId` value as one (no comma-split; free-text); null if empty. */
function decodeRecordIdValue(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return null;
    try {
        return decodeURIComponent(trimmed);
    }
    catch {
        return trimmed;
    }
}
const RECORD_ID_KEY_REGEX = /^recordId\[([^\]]+)\]$/;
/**
 * Parse `recordId[<op>]=...` bracket params from the loose query object. Object
 * ID is not in the shared datatype map, so the shared parser leaves these keys
 * untouched. Disallowed operators (e.g. INCLUDES) are rejected with a 400.
 */
function parseRecordIdFilters(data, ctx) {
    const result = [];
    for (const [key, raw] of Object.entries(data)) {
        const match = key.match(RECORD_ID_KEY_REGEX);
        if (!match || typeof raw !== 'string')
            continue;
        const operator = match[1];
        if (!RECORD_ID_OPERATOR_SET.has(operator)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Unsupported operator for recordId',
                path: ['filters'],
            });
            continue;
        }
        // Is Empty / Is Not Empty carry no value; the raw param is empty, so keep
        // them explicitly rather than dropping on the empty-value check below.
        if (isValuelessOperator(operator)) {
            result.push({
                field: AUDIT_LOGS_FILTERABLE_FIELDS.RECORD_ID,
                operator,
                value: [],
            });
            continue;
        }
        const value = decodeRecordIdValue(raw);
        if (value !== null) {
            result.push({
                field: AUDIT_LOGS_FILTERABLE_FIELDS.RECORD_ID,
                operator,
                value: [value],
            });
        }
    }
    return result;
}
/**
 * Per-value validation for a single audit-logs filter. Rejects malformed
 * operator/value combinations with a 400 (via the query-schema `superRefine`)
 * rather than letting them reach Postgres as a 500.
 */
function validateAuditFilterValue(filter, index, ctx) {
    const addIssue = (message) => {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message,
            path: ['filters', index, 'value'],
        });
    };
    // Is Empty / Is Not Empty carry no value on any column (the parser forces an
    // empty value array), so the per-field value checks below — and the
    // timestamp/recordId operator gates — don't apply.
    if (isValuelessOperator(filter.operator)) {
        return;
    }
    switch (filter.field) {
        case AUDIT_LOGS_FILTERABLE_FIELDS.TIMESTAMP:
            if (!TIMESTAMP_OPERATOR_SET.has(filter.operator)) {
                addIssue('Unsupported operator for timestamp filter');
                return;
            }
            // BETWEEN is [start, end]; other arities silently drop in buildDateClause.
            if (filter.operator === FilterOperators.BETWEEN &&
                filter.value.length !== 2) {
                addIssue('Timestamp BETWEEN requires exactly two date values');
                return;
            }
            if (!filter.value.every(isParseableDate)) {
                addIssue('Invalid date value for timestamp filter');
            }
            return;
        case AUDIT_LOGS_FILTERABLE_FIELDS.OPERATION:
            if (!filter.value.every(v => AUDIT_OPERATION_VALUES.has(v.trim().toUpperCase()))) {
                addIssue('Invalid operation value');
            }
            return;
        case AUDIT_LOGS_FILTERABLE_FIELDS.USER:
            if (!filter.value.every(isUuid)) {
                addIssue('Invalid user id (expected uuid)');
            }
            return;
        case AUDIT_LOGS_FILTERABLE_FIELDS.RECORD_ID:
            // Object ID is a single free-text value — never comma-split — so a
            // multi-value recordId filter is invalid regardless of operator.
            if (filter.value.length !== 1) {
                addIssue('recordId accepts a single value');
                return;
            }
            // Only EQUALS ("Is") requires a full uuid; the contains-family supports
            // partial-UUID free-text matching, so those values are unconstrained.
            if (filter.operator === FilterOperators.EQUALS &&
                !isUuid(filter.value[0] ?? '')) {
                addIssue('Invalid record id (expected uuid for Is)');
            }
            return;
        case AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES:
            if (!filter.value.every(v => CHANGES_VALUE_REGEX.test(v))) {
                addIssue('Invalid changes value (expected tableName[.field])');
            }
            return;
        default:
            return;
    }
}
const auditLogsTableQueryBaseSchema = paginationSchema.extend({
    sortBy: auditLogsTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    knownTotal: knownTotalQuerySchema,
});
/**
 * Backend query schema for the audit-logs DataTable list endpoint. Built on the
 * shared `createTableQuerySchema` (operator × data-type validation + bracket
 * parsing) for the 5 conforming fields, then appends bespoke `recordId` filters
 * and applies per-value validation. `limit` is capped at 100 by
 * `paginationSchema`.
 */
export const auditLogsTableQuerySchema = createTableQuerySchema(auditLogsTableQueryBaseSchema, SHARED_COLUMN_DATA_TYPES)
    .transform((data, ctx) => {
    const recordIdFilters = parseRecordIdFilters(data, ctx);
    return { ...data, filters: [...data.filters, ...recordIdFilters] };
})
    .superRefine((data, ctx) => {
    data.filters.forEach((filter, index) => validateAuditFilterValue(filter, index, ctx));
});
/**
 * Response schema for the audit-logs DataTable list endpoint. Rows reuse the
 * existing `auditLogSchema` unchanged; the envelope is the standard
 * `TableResponse<AuditLog>` shape.
 */
export const auditLogsTableResponseSchema = z.object({
    data: z.array(auditLogSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema,
});
/**
 * API-client params for the list endpoint. `page`/`limit` are optional here;
 * the backend applies defaults.
 */
export const listAuditLogsDataTableParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    sortBy: auditLogsTableSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    search: z.string().optional(),
    filters: z.array(FiltersSchema.element).optional(),
    knownTotal: knownTotalParamsSchema,
});
/**
 * Dynamic filter-option fields for the dispatcher. Discriminator values are the
 * field-key constants — `tableName`/`user`/`changes`, not literals.
 */
export const auditLogsFilterOptionFieldSchema = z.enum([
    AUDIT_LOGS_FILTERABLE_FIELDS.TABLE_NAME,
    AUDIT_LOGS_FILTERABLE_FIELDS.USER,
    AUDIT_LOGS_FILTERABLE_FIELDS.CHANGES,
]);
/**
 * Query schema for the filter-options dispatcher. `limit` is `.max()`-capped to
 * guard against a DoS-sized page.
 */
export const auditLogsFilterOptionsQuerySchema = z.object({
    field: auditLogsFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    offset: z.coerce.number().int().min(0).default(0),
});
/**
 * One filter option, with optional per-field metadata:
 * - `user`: `name`/`email` for the dropdown subtitle (value = user id).
 * - `changes`: `table` + `field` for the `field [table]` renderer.
 * - `tableName`: value === label, no metadata needed.
 */
export const auditLogFilterOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    name: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    table: z.string().optional(),
    field: z.string().nullable().optional(),
});
/**
 * Response schema for the filter-options dispatcher. `options` carries the raw
 * values; `optionsWithMetadata` carries the richer per-field shapes used by the
 * dropdown renderers.
 */
export const auditLogsFilterOptionsResponseSchema = z.object({
    field: auditLogsFilterOptionFieldSchema,
    options: z.array(z.string()),
    optionsWithMetadata: z.array(auditLogFilterOptionSchema).optional(),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
//# sourceMappingURL=audit-logs-table.js.map